/* eslint-disable max-lines */

/* eslint-disable no-underscore-dangle */
import React from 'react';

import {
  ApolloClient,
  ApolloLink,
  ApolloProvider,
  FieldReadFunction,
  HttpLink,
  InMemoryCache,
  Observable,
  defaultDataIdFromObject,
  split,
} from '@apollo/client';
import { BatchHttpLink } from '@apollo/client/link/batch-http';
import { createPersistedQueryLink } from '@apollo/client/link/persisted-queries';
import { getMainDefinition } from '@apollo/client/utilities';
import axios from 'axios';
import { buildAxiosFetch } from 'axios-fetch';
import { sha256 } from 'crypto-hash';
import { print } from 'graphql';
import { createClient } from 'graphql-sse';

import { getJSONWebToken } from 'src/contexts/auth0/utils';

import DevOverrides from 'src/utils/DevOverrides';
import { API_URL } from 'src/utils/api';
import axiosInstance from 'src/utils/axios';
import setupAxiosInstance from 'src/utils/axios/setupAxiosInstance';

import { GraphQLConfig } from 'src/config';

import { ErrorLink } from './ErrorLink';
import getIsRetryEnabled from './utils/getIsRetryEnabled';

const GQL_ENDPOINT_EPHEMERAL_SEPARATE_REQUESTS = new HttpLink({
  uri: `${API_URL}/graphql/`,
  fetch: buildAxiosFetch(axiosInstance),
});

const axiosNoRetry = setupAxiosInstance(axios.create(), { retries: 0 });

const GQL_ENDPOINT_EPHEMERAL_NO_RETRY_REQUESTS = new HttpLink({
  uri: `${API_URL}/graphql/`,
  fetch: buildAxiosFetch(axiosNoRetry),
});

const GQL_ENDPOINT_EPHEMERAL_BATCHED_REQUESTS = new BatchHttpLink({
  uri: `${API_URL}/graphql/batch`,
  batchMax: GraphQLConfig.batchMax, // No more than X operations per batch
  batchInterval: GraphQLConfig.batchInterval, // Wait no more than Yms after first batched operation
  fetch: buildAxiosFetch(axiosInstance),
});

const GQL_ENDPOINT_PERSISTED_BATCHED_REQUESTS = createPersistedQueryLink({ sha256 }).concat(
  GQL_ENDPOINT_EPHEMERAL_BATCHED_REQUESTS
);

const GQL_ENDPOINT_PERSISTED_SEPARATE_REQUESTS = createPersistedQueryLink({ sha256 }).concat(
  GQL_ENDPOINT_EPHEMERAL_SEPARATE_REQUESTS
);

const GQL_ENDPOINT_PERSISTED_NO_RETRY_REQUESTS = createPersistedQueryLink({ sha256 }).concat(
  GQL_ENDPOINT_EPHEMERAL_NO_RETRY_REQUESTS
);

// Create a new link that will choose between the single or batch endpoints + persisted queries
const mLoopEndpoint = new ApolloLink((operation, forward) => {
  // Check operation context for a custom flag
  const context = operation.getContext();
  const isPersistedEnabled = GraphQLConfig.persistedEnabled;
  const isBatchEnabled = GraphQLConfig.batchEnabled;

  // Get whether the specific opeartion wants to use batch or not
  const { batch } = context;

  // Send to single endpoint if:
  // 1. The operation explicitly sets batch: false in context
  // 2. The global batch flag is false
  // 3. The local storage flag is true by user (only available through dev tools)
  const isBatchDisabled = batch === false || !isBatchEnabled || DevOverrides.getIsBatchDisabled();

  const isRetryEnabled = getIsRetryEnabled(operation);

  // Choose the endpoint to use based on batch and persisted setup
  let finalEndpoint: ApolloLink;

  if (isRetryEnabled) {
    if (isPersistedEnabled) {
      // if persisted queries are enabled always hit the persisted endpoint
      if (isBatchDisabled) {
        finalEndpoint = GQL_ENDPOINT_PERSISTED_SEPARATE_REQUESTS;
      } else {
        finalEndpoint = GQL_ENDPOINT_PERSISTED_BATCHED_REQUESTS;
      }

      // if persisted queries are disabled always hit the no persisted endpoint
    } else {
      // eslint-disable-next-line no-lonely-if
      if (isBatchDisabled) {
        finalEndpoint = GQL_ENDPOINT_EPHEMERAL_SEPARATE_REQUESTS;
      } else {
        finalEndpoint = GQL_ENDPOINT_EPHEMERAL_BATCHED_REQUESTS;
      }
    }

    // if retries are disabled always hit the no retry endpoint
  } else {
    // eslint-disable-next-line no-lonely-if
    if (isPersistedEnabled) {
      finalEndpoint = GQL_ENDPOINT_PERSISTED_NO_RETRY_REQUESTS;
    } else {
      finalEndpoint = GQL_ENDPOINT_EPHEMERAL_NO_RETRY_REQUESTS;
    }
  }

  // Forward the operation to the final link
  return finalEndpoint.request(operation, forward);
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getRead: (typeName: string) => FieldReadFunction<any, any> = (typeName: string) => {
  return (_, { args, toReference, readField }) => {
    const foundIds: string[] = [];
    args?.ids
      ?.forEach((id) => {
        const idVal = readField('id', { __ref: `${typeName}:${id}` }) as string;
        if (args?.ids.includes(idVal)) {
          foundIds.push(id);
        }
      })
      ?.filter(Boolean);

    if (args?.ids.length === 0 || args?.ids.length !== foundIds.length) {
      return undefined;
    }

    return args?.ids?.map((id) =>
      toReference({
        __typename: typeName,
        id,
      })
    );
  };
};

// SSE subscription link for GraphQL subscriptions (notifications, activity logs)
const sseClient = createClient({
  url: `${API_URL}/graphql/subscriptions/`,
  credentials: 'include',
  headers: async () => {
    const token = await getJSONWebToken();
    if (token) {
      return { Authorization: `Bearer ${token}` };
    }
    return {} as Record<string, string>;
  },
});

const sseLink = new ApolloLink((operation) => {
  return new Observable((observer) => {
    const dispose = sseClient.subscribe(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      { query: print(operation.query as any), variables: operation.variables },
      {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        next: (value) => observer.next(value as any),
        error: (err) => observer.error(err),
        complete: () => observer.complete(),
      }
    );
    return () => dispose();
  });
});

// Split: subscriptions go to SSE, everything else goes to HTTP
const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
  },
  sseLink,
  ApolloLink.from([ErrorLink, mLoopEndpoint])
);

const client = new ApolloClient({
  defaultOptions: {
    watchQuery: {
      // Skip polling if the tab is inactive
      skipPollAttempt: () => typeof document !== 'undefined' && document.hidden,
    },
  },
  link: splitLink,
  cache: new InMemoryCache({
    possibleTypes: {
      Actor: ['EmployeeActor', 'AgentActor', 'CandidateActor', 'SystemActorV2'],
      AiContextTarget: ['OrganizationAiContextTarget', 'CandidateAiContextTarget', 'EmployeeAiContextTarget'],
      PortalTemplateBlock: [
        'PortalTemplateBlockContentCard',
        'PortalTemplateBlockDivider',
        'PortalTemplateBlockImage',
        'PortalTemplateBlockLinkedCard',
        'PortalTemplateBlockText',
        'PortalTemplateBlockVideo',
      ],
      TaskFlag: [
        'TaskFlagCandidateDeclined',
        'TaskFlagCandidateTentative',
        'TaskFlagDebriefAttendeeDeactivated',
        'TaskFlagDebriefAttendeeTentative',
        'TaskFlagInterviewerDeactivated',
        'TaskFlagInterviewerDeclined',
        'TaskFlagInterviewerTentative',
        'TaskFlagRequestedChange',
        'TaskFlagInterviewerDebriefDeclined',
      ],
      ModuleMemberInterview: ['ModuleMemberApplicationStageInterviewLog', 'ModuleMemberManualLog'],
    },
    typePolicies: {
      JobStageInterview: {
        fields: {
          forcedStartAt: {
            read: () => {
              // Always return undefined, it is a client side field
              return undefined;
            },
          },
        },
      },
      InterviewModuleMember: {
        keyFields: ['interviewModuleId', 'employeeId'],
        fields: {
          stats: {
            merge: true,
          },
        },
      },
      EmployeeNotificationPreference: {
        keyFields: ['employeeId', 'medium', 'type'],
      },
      RecruitingSource: {
        keyFields: ['source'],
      },
      SettingCalendarId: {
        // NOTE:
        // `SettingCalendarId` is a "setting wrapper" value-object (remoteCalendarId + level + remoteCalendar),
        // not a stable entity. Normalizing by `remoteCalendarId` incorrectly merges different settings
        // (across job stages / org vs job-stage level), causing `level` to be overwritten and the UI to
        // incorrectly show "Synced from org settings" on first load.
        keyFields: false,
      },
      SettingTemplateId: {
        // Same issue as `SettingCalendarId`: this is a wrapper (templateId + level + template), not an entity.
        // Normalizing by `templateId` merges settings across contexts and can overwrite `level`.
        keyFields: false,
      },
      Template: {
        fields: {
          attachments: {
            merge(_existing, incoming) {
              return incoming;
            },
          },
        },
      },
      IntegrationCheckHealthResult: {
        fields: {
          google: {
            merge: true,
          },
          microsoft: {
            merge: true,
          },
          microsoftGccHigh: {
            merge: true,
          },
          zoom: {
            merge: true,
          },
          greenhouseV3: {
            merge: true,
          },
          ashby: {
            merge: true,
          },
          lever: {
            merge: true,
          },
          gem: {
            merge: true,
          },
          smartRecruiters: {
            merge: true,
          },
        },
      },
      OrgIntegrations: {
        fields: {
          ashby: {
            merge: true,
          },
          coderpad: {
            merge: true,
          },
          greenhouse: {
            merge: true,
          },
          lever: {
            merge: true,
          },
          smartRecruiters: {
            merge: true,
          },
          google: {
            merge: true,
          },
          microsoft: {
            merge: true,
          },
          microsoftCustom: {
            merge: true,
          },
          microsoftGccHigh: {
            merge: true,
          },
          codesignal: {
            merge: true,
          },
          zoom: {
            merge: true,
          },
          zoomCustom: {
            merge: true,
          },
          slack: {
            merge: true,
          },
          hackerrank: {
            merge: true,
          },
          codility: {
            merge: true,
          },
          gem: {
            merge: true,
          },
          workday: {
            merge: true,
          },
          brighthire: {
            merge: true,
          },
        },
      },
      EmployeeIntegrations: {
        fields: {
          slack: {
            merge: true,
          },
          google: {
            merge: true,
          },
          microsoft: {
            merge: true,
          },
          microsoftCustomEmployee: {
            merge: true,
          },
          microsoftGccHighEmployee: {
            merge: true,
          },
        },
      },
      Query: {
        fields: {
          template: {
            read(_, { args, toReference }) {
              return toReference({
                __typename: 'Template',
                id: args?.id,
              });
            },
          },
          employeeByIds: {
            read: getRead('Employee'),
          },
          jobByIds: {
            read: getRead('Job'),
          },
          templateByIds: {
            read: getRead('Template'),
          },
        },
      },
    },
    dataIdFromObject: (object) => {
      switch (object.__typename) {
        default: {
          const rowID = object.row_id || object.rowID || object.id || object.atsId;
          return rowID ? `${object.__typename}:${rowID}` : defaultDataIdFromObject(object);
        }
      }
    },
  }),
});

// eslint-disable-next-line modernloop/validate-component-definition.cjs
const ApolloProviderX = ({ children }) => <ApolloProvider client={client}>{children}</ApolloProvider>;

export default ApolloProviderX;
